// Unified database service
// Uses Supabase when configured, falls back to localStorage

import { supabase, isSupabaseConfigured } from './supabase';
import { Player } from '../types';
import { generateRandomFace, mixFaces, FaceFeatures } from '../utils/faceGenerator';
import { FUNCTIONS_URL } from '../services/supabaseConfig';
import { LEAGUE_TYPE, QL_ARRAY, TRAD_ARRAY, LEGACY_REGULAR, LEGACY_FAST_1S, LEGACY_FAST_5S } from '../constants/leagueTypes';
import { 
  getItem, setItem, 
  StoredUser, StoredPlayer, StoredTeam, 
  StoredContractOffer, StoredSeason, StoredGameResult, StoredStake 
} from './localStorage';

// ============ USERS ============

export async function createUser(username: string, email: string): Promise<StoredUser> {
  const user: StoredUser = {
    id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    username,
    email,
    players: [],
    createdAt: Date.now()
  };

  if (isSupabaseConfigured() && supabase) {
    const { error } = await supabase.from('users').insert({
      username: user.username,
      email: user.email
    });
    if (error) console.error('Supabase insert error:', error);
  }

  // Also save to localStorage
  const users = getItem<StoredUser[]>('users') || [];
  users.push(user);
  setItem('users', users);

  return user;
}

export async function getUserByEmail(email: string): Promise<StoredUser | null> {
  if (isSupabaseConfigured() && supabase) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .single();
    
    if (!error && data) {
      return {
        id: data.id,
        username: data.username,
        email: data.email,
        players: data.players || [],
        createdAt: new Date(data.created_at).getTime()
      };
    }
  }

  const users = getItem<StoredUser[]>('users') || [];
  return users.find(u => u.email === email) || null;
}

export async function getUserById(id: string): Promise<StoredUser | null> {
  if (isSupabaseConfigured() && supabase) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single();
    
    if (!error && data) {
      return {
        id: data.id,
        username: data.username,
        email: data.email,
        players: data.players || [],
        createdAt: new Date(data.created_at).getTime()
      };
    }
  }

  const users = getItem<StoredUser[]>('users') || [];
  return users.find(u => u.id === id) || null;
}

// ============ PLAYERS ============

export interface CreatePlayerInput {
  userId: string;
  firstName: string;
  lastName: string;
  position: string;
  throwingHand: string;
  battingHand: string;
  age: number;
  allocatedPoints: Record<string, number>;
  baseAttributes?: Record<string, number>;
  // Fallback fields for offline/localStorage mode
  overall?: number;
  attributes?: Record<string, number>;
  face?: any;
  height?: number;
  weight?: number;
}

export async function createPlayer(input: CreatePlayerInput): Promise<StoredPlayer> {
  // ─── Supabase path: use secure edge function ───
  if (isSupabaseConfigured() && supabase) {
    // Get auth session for the user's access token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('You must be signed in to create a player.');
    }

    const response = await fetch(`${FUNCTIONS_URL}/create-player`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        action: 'create',
        firstName: input.firstName,
        lastName: input.lastName,
        position: input.position,
        battingHand: input.battingHand,
        throwingHand: input.throwingHand,
        age: input.age,
        allocatedPoints: input.allocatedPoints,
        baseAttributes: input.baseAttributes,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to create player. Please try again.');
    }

    const serverPlayer = result.player;

    // Build a StoredPlayer from the edge function response
    const newPlayer: StoredPlayer = {
      id: serverPlayer.id,
      userId: serverPlayer.userId,
      firstName: serverPlayer.firstName,
      lastName: serverPlayer.lastName,
      position: serverPlayer.position,
      throwingHand: serverPlayer.throwingHand,
      battingHand: serverPlayer.battingHand,
      height: serverPlayer.height,
      weight: serverPlayer.weight,
      age: serverPlayer.age,
      overall: serverPlayer.overall,
      attributes: serverPlayer.attributes,
      face: serverPlayer.face,
      gender: serverPlayer.gender || 'male',
      createdAt: serverPlayer.createdAt ? new Date(serverPlayer.createdAt).getTime() : Date.now(),
    };

    // Update localStorage for offline caching
    const players = getItem<StoredPlayer[]>('players') || [];
    players.push(newPlayer);
    setItem('players', players);

    // Update user's player list in localStorage
    const users = getItem<StoredUser[]>('users') || [];
    const userIdx = users.findIndex(u => u.id === input.userId);
    if (userIdx >= 0) {
      users[userIdx].players.push(newPlayer.id);
      setItem('users', users);
    }

    // Portrait generation is now handled by the frontend via PortraitPicker
    // after createPlayer returns — user picks from 3 AI-generated options

    return newPlayer;
  }

  // ─── Offline / localStorage fallback ───
  const face = input.face || generateRandomFace();
  const newPlayer: StoredPlayer = {
    userId: input.userId,
    firstName: input.firstName,
    lastName: input.lastName,
    position: input.position,
    throwingHand: input.throwingHand,
    battingHand: input.battingHand,
    height: input.height || 72,
    weight: input.weight || 200,
    age: input.age,
    overall: input.overall || 50,
    attributes: input.attributes || {},
    face,
    id: `player_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    createdAt: Date.now(),
  };

  // Generate contract offers for this player (localStorage AI teams)
  const { generateContractOffers } = await import('../services/localStorage');
  generateContractOffers(newPlayer.id, newPlayer.position);

  // Save to localStorage
  const players = getItem<StoredPlayer[]>('players') || [];
  players.push(newPlayer);
  setItem('players', players);

  // Update user's player list
  const users = getItem<StoredUser[]>('users') || [];
  const userIdx = users.findIndex(u => u.id === input.userId);
  if (userIdx >= 0) {
    users[userIdx].players.push(newPlayer.id);
    setItem('users', users);
  }

  return newPlayer;
}

export async function getPlayersByUser(userId: string): Promise<StoredPlayer[]> {
  if (isSupabaseConfigured() && supabase) {
    const { data, error } = await supabase
      .from('players')
      .select('*')
      .eq('user_id', userId)
      .eq('retired', false);
    
    if (!error && data) {
      return data.map(p => ({
        id: p.id,
        userId: p.user_id,
        firstName: p.first_name,
        lastName: p.last_name,
        position: p.position,
        throwingHand: p.throwing_hand,
        battingHand: p.batting_hand,
        height: p.height,
        weight: p.weight,
        age: p.age,
        overall: p.overall,
        attributes: {
          power: p.power,
          contact: p.contact,
          speed: p.speed,
          fielding: p.fielding,
          arm: p.arm,
          discipline: p.discipline,
          endurance: p.endurance,
          velocity: p.velocity,
          control: p.control,
          movement: p.movement,
          deception: p.deception,
        },
        face: p.bg_color ? {
          bgColor: p.bg_color,
          eyeStyle: p.eye_style,
          eyeColor: p.eye_color || 'white',
          eyeSize: p.eye_size,
          mouthStyle: p.mouth_style,
          mouthColor: p.mouth_color,
          hairStyle: p.hair_style,
          hairColor: p.hair_color,
          facialHairStyle: p.facial_hair_style,
          facialHairColor: p.facial_hair_color,
        } : undefined,
        createdAt: p.created_at ? new Date(p.created_at).getTime() : Date.now(),
        nonTradeable: p.non_tradeable || false,
        retired: p.retired || false,
        equipment: p.equipment || null,
        avatarUrl: p.avatar_url || undefined,
        dna: (p.parent1_id || p.parent2_id || p.generation) ? {
          parent1Id: p.parent1_id || undefined,
          parent2Id: p.parent2_id || undefined,
          generation: p.generation || 1,
        } : undefined,
      }));
    }
  }
  // Supabase not configured — return empty, never fall back to localStorage for player state
  return [];
}

export async function getPlayerById(playerId: string): Promise<StoredPlayer | null> {
  if (isSupabaseConfigured() && supabase) {
    const { data, error } = await supabase
      .from('players')
      .select('*')
      .eq('id', playerId)
      .single();
    
    if (!error && data) {
      return {
        id: data.id,
        userId: data.user_id,
        firstName: data.first_name,
        lastName: data.last_name,
        position: data.position,
        throwingHand: data.throwing_hand,
        battingHand: data.batting_hand,
        height: data.height,
        weight: data.weight,
        age: data.age,
        overall: data.overall,
        gender: data.gender || 'male',
        avatarUrl: data.avatar_url || undefined,
        teamId: data.team_id,
        attributes: {
          power: data.power,
          contact: data.contact,
          speed: data.speed,
          fielding: data.fielding,
          arm: data.arm,
          discipline: data.discipline,
          endurance: data.endurance,
          velocity: data.velocity,
          control: data.control,
          movement: data.movement,
          deception: data.deception,
        },
        face: data.bg_color ? {
          bgColor: data.bg_color,
          eyeStyle: data.eye_style,
          eyeColor: data.eye_color || 'white',
          eyeSize: data.eye_size,
          mouthStyle: data.mouth_style,
          mouthColor: data.mouth_color,
          hairStyle: data.hair_style,
          hairColor: data.hair_color,
          facialHairStyle: data.facial_hair_style,
          facialHairColor: data.facial_hair_color,
        } : undefined,
        createdAt: data.created_at ? new Date(data.created_at).getTime() : Date.now(),
        equipment: data.equipment || null,
        isHitter: data.is_hitter,
        personality: data.personality || null,
        careerSeason: data.career_season || 0,
        retired: data.retired || false,
        // NFT gen0 bloodline
        nft_parent1_id: data.nft_parent1_id || undefined,
        nft_parent2_id: data.nft_parent2_id || undefined,
        nft_parent1_url: data.nft_parent1_url || undefined,
        nft_parent2_url: data.nft_parent2_url || undefined,
        dna: (data.parent1_id || data.parent2_id || data.generation) ? {
          parent1Id: data.parent1_id || undefined,
          parent2Id: data.parent2_id || undefined,
          generation: data.generation || 1,
        } : undefined,
      };
    }
  }
  // Supabase not configured — return null, never fall back to localStorage for player state
  return null;
}

export async function retirePlayer(playerId: string): Promise<void> {
  if (isSupabaseConfigured() && supabase) {
    await supabase.from('players').update({ retired: true, retired_at: Date.now() }).eq('id', playerId);
  }
  // No localStorage write — Supabase is the source of truth
}

export async function getRetiredPlayers(userId: string): Promise<StoredPlayer[]> {
  if (isSupabaseConfigured() && supabase) {
    const { data, error } = await supabase
      .from('players')
      .select('*')
      .eq('user_id', userId)
      .eq('retired', true);
    if (!error && data) {
      return data.map(p => ({
        id: p.id,
        userId: p.user_id,
        firstName: p.first_name,
        lastName: p.last_name,
        position: p.position,
        throwingHand: p.throwing_hand,
        battingHand: p.batting_hand,
        height: p.height,
        weight: p.weight,
        age: p.age,
        overall: p.overall,
        retired: p.retired || false,
        retiredAt: p.retired_at ? new Date(p.retired_at).getTime() : undefined,
        attributes: {
          power: p.power,
          contact: p.contact,
          speed: p.speed,
          fielding: p.fielding,
          arm: p.arm,
          discipline: p.discipline,
          endurance: p.endurance,
          velocity: p.velocity,
          control: p.control,
          movement: p.movement,
          deception: p.deception,
        },
        avatarUrl: p.avatar_url || undefined,
        gender: p.gender || 'male',
        careerSeason: p.career_season || 0,
        nonTradeable: p.non_tradeable || false,
        equipment: p.equipment || null,
        createdAt: p.created_at ? new Date(p.created_at).getTime() : Date.now(),
      }));
    }
  }
  // Supabase not configured — return empty, never fall back to localStorage
  return [];
}

// ============ FREE AGENTS ============

// Release player to free agency (for AI teams or human teams)
export async function releaseToFreeAgency(playerId: string): Promise<StoredPlayer | null> {
  const players = getItem<StoredPlayer[]>('players') || [];
  const idx = players.findIndex(p => p.id === playerId);
  
  if (idx < 0) return null;
  
  // Clear team from contract (make them a free agent)
  players[idx] = {
    ...players[idx],
    teamId: undefined,
    contract: players[idx].contract ? {
      ...players[idx].contract!,
      teamId: '',
      teamName: 'Free Agent',
    } : undefined
  };
  
  setItem('players', players);
  
  // Also update in Supabase if configured
  if (isSupabaseConfigured() && supabase) {
    await supabase.from('players').update({
      team_id: null,
      team_name: null
    }).eq('id', playerId);
  }
  
  return players[idx];
}

// Get all free agents (players without a team)
export async function getFreeAgents(): Promise<StoredPlayer[]> {
  const players = getItem<StoredPlayer[]>('players') || [];
  return players.filter(p => !p.teamId && !p.retired);
}

// Get free agents by position
export async function getFreeAgentsByPosition(position: string): Promise<StoredPlayer[]> {
  const freeAgents = await getFreeAgents();
  return freeAgents.filter(p => p.position === position);
}

// ============ CONTRACT OFFERS ============

const TIER_NAMES = ['', 'Elite', 'Platinum', 'Gold', 'Silver', 'Bronze'];

export async function saveContractOffers(playerId: string, offers: StoredContractOffer[]): Promise<void> {
  if (isSupabaseConfigured() && supabase) {
    await supabase.from('contract_offers').delete().eq('player_id', playerId);
    
    const toInsert = offers.map(offer => ({
      player_id: offer.playerId,
      player_position: offer.playerPosition,
      team_id: offer.teamId,
      team_name: offer.teamName,
      team_city: offer.teamCity,
      tier: offer.tier,
      tier_name: offer.tierName,
      salary: offer.salary,
      duration: offer.duration,
      bonuses: offer.bonuses,
      scout_report: offer.scoutReport,
      status: offer.status,
      expires_at: new Date(offer.expiresAt).toISOString()
    }));
    
    await supabase.from('contract_offers').insert(toInsert);
  }

  const allOffers = getItem<StoredContractOffer[]>('contractOffers') || [];
  const filtered = allOffers.filter(o => o.playerId !== playerId);
  setItem('contractOffers', [...filtered, ...offers]);
}

export async function getContractOffersFromDb(playerId: string): Promise<StoredContractOffer[]> {
  if (isSupabaseConfigured() && supabase) {
    const { data, error } = await supabase
      .from('contract_offers')
      .select('*')
      .eq('player_id', playerId);
    
    if (!error && data) {
      return data.map(o => ({
        id: o.id,
        playerId: o.player_id,
        playerPosition: o.player_position,
        teamId: o.team_id,
        teamName: o.team_name,
        teamCity: o.team_city,
        tier: o.tier,
        tierName: o.tier_name || TIER_NAMES[o.tier] || 'Bronze',
        salary: o.salary,
        duration: o.duration,
        bonuses: o.bonuses,
        scoutReport: o.scout_report,
        status: o.status,
        createdAt: new Date(o.created_at).getTime(),
        expiresAt: new Date(o.expires_at).getTime()
      }));
    }
  }

  const allOffers = getItem<StoredContractOffer[]>('contractOffers') || [];
  return allOffers.filter(o => o.playerId === playerId);
}

export async function updateOfferStatus(offerId: string, status: string): Promise<void> {
  if (isSupabaseConfigured() && supabase) {
    await supabase.from('contract_offers').update({ status }).eq('id', offerId);
  }

  const allOffers = getItem<StoredContractOffer[]>('contractOffers') || [];
  const updated = allOffers.map(o => o.id === offerId ? { ...o, status: status as any } : o);
  setItem('contractOffers', updated);
}

// ============ TEAMS ============

export async function getAllTeamsFromDb(): Promise<StoredTeam[]> {
  if (isSupabaseConfigured() && supabase) {
    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .order('tier', { ascending: true })
      .order('rating', { ascending: false });
    
    if (!error && data && data.length > 0) {
      return data.map(t => ({
        id: t.id,
        name: t.name,
        city: t.city,
        abbreviation: t.abbreviation,
        tier: t.tier,
        tierName: t.tier_name,
        rating: t.rating,
        wins: t.wins,
        losses: t.losses,
        runsScored: t.runs_scored,
        runsAllowed: t.runs_allowed,
        streak: t.streak || '',
        streakCount: t.streak_count || 0,
        revenue: t.revenue || 0,
        attendance: t.attendance || 0,
        ownerId: t.owner_id,
        stakers: t.stakers || []
      }));
    }
  }

  // Fallback to localStorage
  const localTeams = getItem<StoredTeam[]>('teams');
  if (localTeams && localTeams.length > 0) return localTeams;
  
  // Generate teams if none exist
  const { generateAllTeams } = await import('./localStorage');
  return generateAllTeams();
}

export async function getTeamByIdDb(teamId: string): Promise<StoredTeam | null> {
  if (isSupabaseConfigured() && supabase) {
    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .eq('id', teamId)
      .single();
    
    if (!error && data) {
      return {
        id: data.id,
        name: data.name,
        city: data.city,
        abbreviation: data.abbreviation,
        tier: data.tier,
        tierName: data.tier_name,
        rating: data.rating,
        wins: data.wins,
        losses: data.losses,
        runsScored: data.runs_scored,
        runsAllowed: data.runs_allowed,
        streak: data.streak || '',
        streakCount: data.streak_count || 0,
        revenue: data.revenue || 0,
        attendance: data.attendance || 0,
        ownerId: data.owner_id,
        stakers: data.stakers || []
      };
    }
  }

  const teams = getItem<StoredTeam[]>('teams') || [];
  return teams.find(t => t.id === teamId) || null;
}

export async function updateTeamDb(teamId: string, updates: Partial<StoredTeam>): Promise<StoredTeam | null> {
  if (isSupabaseConfigured() && supabase) {
    const dbUpdates: Record<string, any> = {};
    if (updates.wins !== undefined) dbUpdates.wins = updates.wins;
    if (updates.losses !== undefined) dbUpdates.losses = updates.losses;
    if (updates.runsScored !== undefined) dbUpdates.runs_scored = updates.runsScored;
    if (updates.runsAllowed !== undefined) dbUpdates.runs_allowed = updates.runsAllowed;
    if (updates.rating !== undefined) dbUpdates.rating = updates.rating;
    if (updates.revenue !== undefined) dbUpdates.revenue = updates.revenue;
    if (updates.attendance !== undefined) dbUpdates.attendance = updates.attendance;
    if (updates.streak !== undefined) dbUpdates.streak = updates.streak;
    if (updates.streakCount !== undefined) dbUpdates.streak_count = updates.streakCount;
    if (updates.ownerId !== undefined) dbUpdates.owner_id = updates.ownerId;
    
    await supabase.from('teams').update(dbUpdates).eq('id', teamId);
  }

  // Also update localStorage
  const teams = getItem<StoredTeam[]>('teams') || [];
  const index = teams.findIndex(t => t.id === teamId);
  if (index === -1) return null;
  teams[index] = { ...teams[index], ...updates };
  setItem('teams', teams);
  return teams[index];
}

// ============ FORGE / BREEDING ============

export interface StoredForgeListing {
  id: string;
  playerId: string;
  ownerId: string;
  listingPrice: number;
  createdAt: number;
  expiresAt: number;
  isActive: boolean;
  breedCountThisSeason: number;
}

export interface StoredBreed {
  id: string;
  parent1Id: string;
  parent2Id: string;
  childId: string;
  breederId: string;
  season: number;
  createdAt: number;
}

// Create a forge listing
export async function createForgeListing(playerId: string, ownerId: string, price: number): Promise<StoredForgeListing> {
  const listing: StoredForgeListing = {
    id: `forge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    playerId,
    ownerId,
    listingPrice: price,
    createdAt: Date.now(),
    expiresAt: Date.now() + 90 * 24 * 60 * 60 * 1000, // 90 days
    isActive: true,
    breedCountThisSeason: 0,
  };

  if (isSupabaseConfigured() && supabase) {
    await supabase.from('forge_listings').insert({
      id: listing.id,
      player_id: listing.playerId,
      owner_id: listing.ownerId,
      listing_price: listing.listingPrice,
      created_at: new Date(listing.createdAt).toISOString(),
      expires_at: new Date(listing.expiresAt).toISOString(),
      is_active: listing.isActive,
      breed_count_this_season: 0,
    });
  }

  // LocalStorage fallback
  const listings = getItem<StoredForgeListing[]>('forgeListings') || [];
  listings.push(listing);
  setItem('forgeListings', listings);

  return listing;
}

// Get all active forge listings
export async function getActiveForgeListings(): Promise<StoredForgeListing[]> {
  if (isSupabaseConfigured() && supabase) {
    const { data, error } = await supabase
      .from('forge_listings')
      .select('*')
      .eq('is_active', true)
      .gt('expires_at', new Date().toISOString());
    
    if (!error && data) {
      return data.map(l => ({
        id: l.id,
        playerId: l.player_id,
        ownerId: l.owner_id,
        listingPrice: l.listing_price,
        createdAt: new Date(l.created_at).getTime(),
        expiresAt: new Date(l.expires_at).getTime(),
        isActive: l.is_active,
        breedCountThisSeason: l.breed_count_this_season,
      }));
    }
  }

  const listings = getItem<StoredForgeListing[]>('forgeListings') || [];
  const now = Date.now();
  return listings.filter(l => l.isActive && l.expiresAt > now);
}

// Get listings by owner
export async function getForgeListingsByOwner(ownerId: string): Promise<StoredForgeListing[]> {
  if (isSupabaseConfigured() && supabase) {
    const { data, error } = await supabase
      .from('forge_listings')
      .select('*')
      .eq('owner_id', ownerId)
      .order('created_at', { ascending: false });
    
    if (!error && data) {
      return data.map(l => ({
        id: l.id,
        playerId: l.player_id,
        ownerId: l.owner_id,
        listingPrice: l.listing_price,
        createdAt: new Date(l.created_at).getTime(),
        expiresAt: new Date(l.expires_at).getTime(),
        isActive: l.is_active,
        breedCountThisSeason: l.breed_count_this_season,
      }));
    }
  }

  const listings = getItem<StoredForgeListing[]>('forgeListings') || [];
  return listings.filter(l => l.ownerId === ownerId);
}

// Cancel a forge listing
export async function cancelForgeListing(listingId: string): Promise<void> {
  if (isSupabaseConfigured() && supabase) {
    await supabase.from('forge_listings').update({ is_active: false }).eq('id', listingId);
  }

  const listings = getItem<StoredForgeListing[]>('forgeListings') || [];
  const idx = listings.findIndex(l => l.id === listingId);
  if (idx >= 0) {
    listings[idx].isActive = false;
    setItem('forgeListings', listings);
  }
}

// Record a breed (breeding transaction)
export async function recordBreed(
  parent1Id: string,
  parent2Id: string,
  childId: string,
  breederId: string,
  season: number
): Promise<StoredBreed> {
  const breed: StoredBreed = {
    id: `breed_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    parent1Id,
    parent2Id,
    childId,
    breederId,
    season,
    createdAt: Date.now(),
  };

  if (isSupabaseConfigured() && supabase) {
    await supabase.from('forge_breeds').insert({
      id: breed.id,
      parent1_id: breed.parent1Id,
      parent2_id: breed.parent2Id,
      child_id: breed.childId,
      breeder_id: breed.breederId,
      season: breed.season,
      created_at: new Date(breed.createdAt).toISOString(),
    });
  }

  const breeds = getItem<StoredBreed[]>('forgeBreeds') || [];
  breeds.push(breed);
  setItem('forgeBreeds', breeds);

  return breed;
}

// Get breeds by breeder (to check if they've received a player this season)
export async function getBreedsByBreeder(breederId: string, season: number): Promise<StoredBreed[]> {
  if (isSupabaseConfigured() && supabase) {
    const { data, error } = await supabase
      .from('forge_breeds')
      .select('*')
      .eq('breeder_id', breederId)
      .eq('season', season);
    
    if (!error && data) {
      return data.map(b => ({
        id: b.id,
        parent1Id: b.parent1_id,
        parent2Id: b.parent2_id,
        childId: b.child_id,
        breederId: b.breeder_id,
        season: b.season,
        createdAt: new Date(b.created_at).getTime(),
      }));
    }
  }

  const breeds = getItem<StoredBreed[]>('forgeBreeds') || [];
  return breeds.filter(b => b.breederId === breederId && b.season === season);
}

// Get player's ancestry (for bloodline view)
export async function getPlayerAncestry(playerId: string): Promise<{ parents: Player[], grandparents: Player[], greatGrandparents: Player[] }> {
  const player = await getPlayerById(playerId);
  if (!player?.dna) {
    return { parents: [], grandparents: [], greatGrandparents: [] };
  }

  const getPlayer = async (id: string) => {
    if (!id) return null;
    const p = await getPlayerById(id);
    if (!p) return null;
    // Convert to Player type
    return {
      id: p.id,
      name: `${p.firstName} ${p.lastName}`,
      firstName: p.firstName,
      lastName: p.lastName,
      position: p.position as any,
      overall: p.overall,
      attributes: p.attributes as any,
      stats: { gamesPlayed: 0, atBats: 0, hits: 0, homeRuns: 0, rbi: 0, battingAvg: 0, ops: 0, stolenBases: 0 },
      contract: { status: 'signed' as const, salary: 0, duration: 0, seasonsRemaining: 0 },
      upcomingGames: [],
      dna: p.dna,
    } as any;
  };

  const parents: any[] = [];
  if (player.dna.parent1Id) parents.push(await getPlayer(player.dna.parent1Id));
  if (player.dna.parent2Id) parents.push(await getPlayer(player.dna.parent2Id));

  const grandparents: any[] = [];
  const greatGrandparents: any[] = [];

  for (const parent of parents) {
    if (parent?.dna) {
      if (parent.dna.parent1Id) grandparents.push(await getPlayer(parent.dna.parent1Id));
      if (parent.dna.parent2Id) grandparents.push(await getPlayer(parent.dna.parent2Id));
    }
  }

  for (const gp of grandparents) {
    if (gp?.dna) {
      if (gp.dna.parent1Id) greatGrandparents.push(await getPlayer(gp.dna.parent1Id));
      if (gp.dna.parent2Id) greatGrandparents.push(await getPlayer(gp.dna.parent2Id));
    }
  }

  return {
    parents: parents.filter(Boolean),
    grandparents: grandparents.filter(Boolean),
    greatGrandparents: greatGrandparents.filter(Boolean),
  };
}

// Check if two players are related within 3 generations
export function arePlayersRelated(dna1: any, dna2: any): boolean {
  if (!dna1 || !dna2) return false;
  
  const gen1 = dna1.generation || 1;
  const gen2 = dna2.generation || 1;
  
  if (gen1 === 1 && gen2 === 1) return false;
  
  if (dna1.parent1Id === dna2.id || dna1.parent2Id === dna2.id) return true;
  if (dna2.parent1Id === dna1.id || dna2.parent2Id === dna1.id) return true;
  
  const dna2Grandparents = [dna2.parent1Id, dna2.parent2Id];
  if (dna1.parent1Id && dna2Grandparents.includes(dna1.parent1Id)) return true;
  if (dna1.parent2Id && dna2Grandparents.includes(dna1.parent2Id)) return true;
  
  const dna1Grandparents = [dna1.parent1Id, dna1.parent2Id];
  if (dna2.parent1Id && dna1Grandparents.includes(dna2.parent1Id)) return true;
  if (dna2.parent2Id && dna1Grandparents.includes(dna2.parent2Id)) return true;
  
  return false;
}

// Create a forged child from two parents
export async function createForgedChild(
  parent1: StoredPlayer,
  parent2: StoredPlayer,
  breederId: string,
  season: number
): Promise<{ child: StoredPlayer }> {
  const parent1Gen = parent1.dna?.generation || 1;
  const parent2Gen = parent2.dna?.generation || 1;
  const newGeneration = Math.max(parent1Gen, parent2Gen) + 1;
  

  const mixRatio = 0.4 + Math.random() * 0.2;
  
  const mixedAttributes: Record<string, number> = {};
  const parent1Attrs = parent1.attributes;
  const parent2Attrs = parent2.attributes;
  
  // Same-position bonus
  const samePosition = parent1.position === parent2.position;
  
  const allKeys = Array.from(new Set([...Object.keys(parent1Attrs), ...Object.keys(parent2Attrs)]));
  
  for (const key of allKeys) {
    const val1 = parent1Attrs[key] || 50;
    const val2 = parent2Attrs[key] || 50;
    const baseValue = Math.round(val1 * mixRatio + val2 * (1 - mixRatio));
    const variance = Math.round(baseValue * 0.1 * (Math.random() - 0.5) * 2);
    let finalValue = baseValue + variance;
    
    // Same-position parents: boost position-specific attributes
    if (samePosition) {
      const hitterBoost = ['power', 'contact', 'speed', 'fielding', 'arm', 'discipline'];
      const pitcherBoost = ['velocity', 'control', 'movement', 'deception', 'endurance'];
      const isPitcherPos = parent1.position === 'P';
      const boostAttrs = isPitcherPos ? pitcherBoost : hitterBoost;
      if (boostAttrs.includes(key)) {
        finalValue += 3 + Math.floor(Math.random() * 3); // +3 to +5
      }
    }
    
    mixedAttributes[key] = Math.max(10, Math.min(99, finalValue));
  }
  
  const attrValues = Object.values(mixedAttributes);
  const avgAttr = attrValues.reduce((a, b) => a + b, 0) / attrValues.length;
  const newOverall = Math.round(avgAttr);
  
  const childFirstName = parent1.firstName.slice(0, Math.floor(parent1.firstName.length / 2)) + 
                         parent2.firstName.slice(Math.floor(parent2.firstName.length / 2));
  const childLastName = parent1.lastName;
  
  // Mix faces from both parents
  const childFace = parent1.face && parent2.face 
    ? mixFaces(parent1.face as FaceFeatures, parent2.face as FaceFeatures) 
    : generateRandomFace();
  
  const child: StoredPlayer = {
    id: `player_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    userId: breederId,
    firstName: childFirstName || 'Junior',
    lastName: childLastName || 'Player',
    position: parent1.position,
    attributes: mixedAttributes,
    throwingHand: parent1.throwingHand,
    battingHand: parent2.battingHand,
    height: Math.round((parent1.height + parent2.height) / 2),
    weight: Math.round((parent1.weight + parent2.weight) / 2),
    age: 18,
    overall: newOverall,
    dna: {
      generation: newGeneration,
      parent1Id: parent1.id,
      parent2Id: parent2.id,
      breedCountThisSeason: 0,
      forgeSeasonsRemaining: 10,
    },
    face: childFace,
    createdAt: Date.now(),
  };
  
  if (isSupabaseConfigured() && supabase) {
    await supabase.from('players').insert({
      id: child.id,
      user_id: child.userId,
      first_name: child.firstName,
      last_name: child.lastName,
      position: child.position,
      throwing_hand: child.throwingHand,
      batting_hand: child.battingHand,
      height: child.height,
      weight: child.weight,
      age: child.age,
      overall: child.overall,
      attributes: child.attributes,
      parent1_id: parent1.id,
      parent2_id: parent2.id,
      generation: newGeneration,
      forge_seasons_remaining: 10,
      bg_color: childFace.bgColor,
      eye_style: childFace.eyeStyle,
      eye_color: childFace.eyeColor,
      eye_size: childFace.eyeSize,
      mouth_style: childFace.mouthStyle,
      mouth_color: childFace.mouthColor,
      hair_style: childFace.hairStyle,
      hair_color: childFace.hairColor,
      facial_hair_style: childFace.facialHairStyle,
      facial_hair_color: childFace.facialHairColor,
    });
  }
  
  const players = getItem<StoredPlayer[]>('players') || [];
  players.push(child);
  setItem('players', players);
  
  await recordBreed(parent1.id, parent2.id, child.id, breederId, season);
  
  return { child };
}

// ============ LEAGUE FUNCTIONS ============

export interface StoredLeague {
  id: string;
  name: string;
  tier: number;
  season_id?: string;
  status: 'upcoming' | 'active' | 'completed' | 'paused' | 'off_season' | 'pre_season' | 'playoffs' | 'drafting' | 'free_agency';
  start_date?: string;
  end_date?: string;
  league_set_id?: string;
  is_ai_run?: boolean;
  entry_fee?: number;
  season_prizes?: { position: number; amount: number }[];
  // New fields for league types
  league_type?: typeof LEAGUE_TYPE.TRADITIONAL | typeof LEAGUE_TYPE.QUICK | typeof LEGACY_REGULAR | typeof LEGACY_FAST_1S | typeof LEGACY_FAST_5S | typeof LEAGUE_TYPE[keyof typeof LEAGUE_TYPE] | 'fast_12' | 'fast_24';
  seasons_remaining?: number;
  current_season?: number;
  total_seasons?: number;
  total_prize_pool?: number;
  prize_paid_out?: boolean;
  is_engaged?: boolean;  // Per-league clutch
  season_year?: number;
  current_day?: number;
  max_teams?: number;
  sim_speed?: number;
  champion_team_id?: string | null;
  champion_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface StoredLeagueSet {
  id: string;
  name: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  is_active: boolean;
  created_at: string;
}

export interface StoredSimulationState {
  id: string;
  is_running: boolean;
  current_day: number;
  speed: string;
  sim_speed?: string;
  last_updated: string;
  last_tick_at?: string;
  off_season_start?: string;
  skip_off_season?: boolean;
}

// Create a new league
export async function createLeague(
  name: string, 
  tier: number, 
  leagueSetId?: string,
  isAiRun?: boolean,
  leagueType: typeof LEAGUE_TYPE.TRADITIONAL | typeof LEAGUE_TYPE.QUICK | typeof LEGACY_FAST_1S | typeof LEGACY_FAST_5S = LEAGUE_TYPE.TRADITIONAL,
  totalPrizePool?: number
): Promise<StoredLeague> {
  const seasonsRemaining = leagueType === LEAGUE_TYPE.TRADITIONAL ? undefined : (leagueType === LEGACY_FAST_1S ? 1 : 5);
  
  const league: StoredLeague = {
    id: `league_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name,
    tier,
    status: 'upcoming',
    league_set_id: leagueSetId || 'default_set',
    is_ai_run: isAiRun || false,
    league_type: leagueType,
    seasons_remaining: seasonsRemaining,
    current_season: 1,
    total_seasons: leagueType === LEAGUE_TYPE.TRADITIONAL ? undefined : (leagueType === LEGACY_FAST_1S ? 1 : 5),
    total_prize_pool: totalPrizePool,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  if (isSupabaseConfigured() && supabase) {
    await supabase.from('leagues').insert(league);
  } else {
    const leagues = getItem<StoredLeague[]>('leagues') || [];
    leagues.push(league);
    setItem('leagues', leagues);
  }

  return league;
}

// Get all leagues
export async function getAllLeagues(): Promise<StoredLeague[]> {
  if (isSupabaseConfigured() && supabase) {
    const { data } = await supabase.from('leagues').select('*').order('tier');
    return data || [];
  }
  return getItem<StoredLeague[]>('leagues') || [];
}

// Get leagues by tier
export async function getLeaguesByTier(tier: number): Promise<StoredLeague[]> {
  const all = await getAllLeagues();
  return all.filter(l => l.tier === tier);
}

// Get active leagues
export async function getActiveLeagues(): Promise<StoredLeague[]> {
  const all = await getAllLeagues();
  return all.filter(l => l.status === 'active');
}

// Update league status
export async function updateLeagueStatus(
  leagueId: string, 
  status: 'upcoming' | 'active' | 'completed' | 'paused'
): Promise<void> {
  if (isSupabaseConfigured() && supabase) {
    await supabase.from('leagues').update({ 
      status, 
      updated_at: new Date().toISOString() 
    }).eq('id', leagueId);
  } else {
    const leagues = getItem<StoredLeague[]>('leagues') || [];
    const idx = leagues.findIndex(l => l.id === leagueId);
    if (idx >= 0) {
      leagues[idx].status = status;
      leagues[idx].updated_at = new Date().toISOString();
      setItem('leagues', leagues);
    }
  }
}

// League set functions
export async function createLeagueSet(
  name: string, 
  description?: string,
  startDate?: string,
  endDate?: string
): Promise<StoredLeagueSet> {
  const set: StoredLeagueSet = {
    id: `league_set_${Date.now()}`,
    name,
    description,
    start_date: startDate,
    end_date: endDate,
    is_active: false,
    created_at: new Date().toISOString(),
  };

  if (isSupabaseConfigured() && supabase) {
    await supabase.from('league_sets').insert(set);
  } else {
    const sets = getItem<StoredLeagueSet[]>('leagueSets') || [];
    sets.push(set);
    setItem('leagueSets', sets);
  }

  return set;
}

export async function getAllLeagueSets(): Promise<StoredLeagueSet[]> {
  if (isSupabaseConfigured() && supabase) {
    const { data } = await supabase.from('league_sets').select('*').order('created_at');
    return data || [];
  }
  return getItem<StoredLeagueSet[]>('leagueSets') || [];
}

// Simulation state functions
export async function getSimulationState(): Promise<StoredSimulationState> {
  if (isSupabaseConfigured() && supabase) {
    const { data } = await supabase.from('simulation_state').select('*').eq('id', 'global').single();
    if (data) return data;
  }
  
  // Default state
  return {
    id: 'global',
    is_running: false,
    current_day: 0,
    speed: 'normal',
    last_updated: new Date().toISOString()
  };
}

export async function updateSimulationState(
  updates: Partial<StoredSimulationState>
): Promise<void> {
  const state = { 
    ...await getSimulationState(),
    ...updates,
    last_updated: new Date().toISOString()
  };

  if (isSupabaseConfigured() && supabase) {
    await supabase.from('simulation_state').upsert(state);
  } else {
    setItem('simulationState', state);
  }
}

export async function startSimulation(): Promise<void> {
  await updateSimulationState({ is_running: true });
}

export async function stopSimulation(): Promise<void> {
  await updateSimulationState({ is_running: false });
}

export async function setSimulationSpeed(
  speed: 'slow' | 'normal' | 'fast' | 'instant'
): Promise<void> {
  await updateSimulationState({ speed });
}

export async function advanceSimulationDay(): Promise<number> {
  const state = await getSimulationState();
  const newDay = state.current_day + 1;
  await updateSimulationState({ current_day: newDay });
  return newDay;
}

// ============ PORTRAIT GENERATION ============

const SUPABASE_FUNCTION_URL = `${FUNCTIONS_URL}/generate-portrait`;

export interface PortraitTrioResult {
  success: boolean;
  playerId: string;
  choices: string[];
  slots: number[];
  errors?: string[];
}

/** Generate 3 portrait options for a player to choose from */
export async function generatePortraitTrio(
  playerId: string,
  playerName: string,
  position: string,
  isHitter: boolean,
  gender?: string
): Promise<PortraitTrioResult> {
  const { data: { session } } = await supabase!.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

  const response = await fetch(SUPABASE_FUNCTION_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      action: 'generate-trio',
      playerId,
      playerName,
      position,
      isHitter,
      gender,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || `Portrait generation failed (${response.status})`);
  }

  const data = await response.json();
  // Backend now returns choices as [{ slot: 1, url: "..." }, ...]
  // Convert to the format PortraitPicker expects
  return {
    success: data.success,
    playerId: data.playerId,
    choices: (data.choices || []).map((c: any) => c.url),
    slots: (data.choices || []).map((c: any) => c.slot),
    errors: data.errors,
  };
}

/** Select one portrait from the trio and finalize it */
export async function selectPortrait(playerId: string, slot: number): Promise<{ success: boolean; avatarUrl: string }> {
  const { data: { session } } = await supabase!.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

  const response = await fetch(SUPABASE_FUNCTION_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      action: 'select-portrait',
      playerId,
      slot,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || `Portrait selection failed (${response.status})`);
  }

  return response.json();
}

// ─── Client-side Egg Portrait Generation ───
// Replicate Reve Remix blocks server IPs, so we run it from the browser.
// Uses actual parent/grandparent avatars for genuine pedigree blending.

// REPLICATE_KEY must be set via env var VITE_REPLICATE_API_TOKEN at build time
const REPLICATE_KEY = import.meta.env.VITE_REPLICATE_API_TOKEN || '';

export async function generateEggPortraitBrowser(
  motherAvatar: string,
  fatherAvatar: string,
  grandparentAvatars: string[],
  gender: string
): Promise<string> {
  const refImages = [motherAvatar, fatherAvatar, ...grandparentAvatars].slice(0, 4);
  // Reve Remix blends images — keep prompt minimal, just output format
  const genderTag = gender === 'female' ? 'female' : 'male';
  const prompt = `${genderTag} baseball player`;

  // Route through Hetzner proxy — Replicate blocks cloud IPs + has no browser CORS headers
  const res = await fetch(REVE_PROXY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'create', prompt, parent1Url: refImages[0], parent2Url: refImages[1] }),
  });
  if (!res.ok) throw new Error(`Reve proxy error ${res.status}`);

  const prediction = await res.json();
  if (prediction.error) throw new Error('Reve Remix: ' + prediction.error);
  if (prediction.status === 'succeeded') {
    const output = prediction.output;
    return Array.isArray(output) ? output[0] : output;
  }

  const predictionId = prediction.id;
  for (let i = 0; i < 30; i++) {
    await new Promise(r => setTimeout(r, 2000));
    const pr = await fetch(REVE_PROXY, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'poll', predictionId }),
    });
    const result = await pr.json();
    if (result.status === 'succeeded') {
      const url = result.output;
      return Array.isArray(url) ? url[0] : url;
    }
    if (result.status === 'failed') throw new Error('Prediction failed: ' + (result.error || 'unknown'));
  }
  throw new Error('Portrait generation timed out');
}

// ─── Buy Player Portrait Generation ───
// Generates 2 Reve Remix portraits from 2 NFT parent images for the buy flow.
// Returns [portraitUrl1, portraitUrl2] for the user to choose from.

// Reve Remix blends reference images — prompt should be minimal, just output format.
// The gen0 NFT parents ARE the content; style_strength_ratio 50 balances both.
export async function generateBuyPortraits(
  parent1Url: string,
  parent2Url: string,
  playerName: string,
  position: string,
  gender: string
): Promise<[string, string]> {
  const genderTag = gender === 'female' ? 'female' : 'male';
  // Reve Remix works best with short style hint — the reference images carry the content
  const styleHint = `${genderTag} baseball player`;

  const [url1, url2] = await Promise.all([
    generateOnePortrait(styleHint, parent1Url, parent2Url),
    generateOnePortrait(styleHint, parent1Url, parent2Url),
  ]);
  return [url1, url2];
}

// Reve Remix proxy — routes through Hetzner VPS (residential IP)
// Replicate blocks Supabase/cloud IPs and has no CORS headers for browser calls
const REVE_PROXY = 'https://reve.simforgebaseball.com';

async function generateOnePortrait(prompt: string, ref1: string, ref2: string): Promise<string> {
  // Create prediction via proxy
  const res = await fetch(`${REVE_PROXY}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'create', prompt, parent1Url: ref1, parent2Url: ref2 }),
  });
  if (!res.ok) throw new Error(`Reve proxy error ${res.status}`);

  const prediction = await res.json();
  if (prediction.error) throw new Error('Reve Remix: ' + prediction.error);

  // If already done (Prefer: wait)
  if (prediction.status === 'succeeded') {
    const url = prediction.output;
    return Array.isArray(url) ? url[0] : url;
  }

  const predictionId = prediction.id;

  // Poll via proxy
  for (let i = 0; i < 30; i++) {
    await new Promise(r => setTimeout(r, 2000));
    const pr = await fetch(`${REVE_PROXY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'poll', predictionId }),
    });
    const result = await pr.json();
    if (result.status === 'succeeded') {
      const url = result.output;
      return Array.isArray(url) ? url[0] : url;
    }
    if (result.status === 'failed') throw new Error('Prediction failed: ' + (result.error || 'unknown'));
  }
  throw new Error('Portrait generation timed out');
}

/** Upload client-generated portrait to server for egg */
export async function saveEggPortrait(eggId: string, imageUrl: string): Promise<{ success: boolean }> {
  const { data: { session } } = await supabase!.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

  const res = await fetch(`${FUNCTIONS_URL}/breed`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ action: 'save-portrait', eggId, imageUrl }),
  });
  if (!res.ok) throw new Error(`save-portrait failed (${res.status})`);
  return res.json();
}
